import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import UnitManagementPage from '../components/UnitManagementPage'
import { useAuth } from '../hooks/useAuth'
import { api } from '../lib/api'
import { GraduationCap, Users, UserCircle, Database, Calendar, ChevronDown, AlertCircle } from 'lucide-react'
import SchoolYearTransitionTab from '../components/SchoolYearTransitionTab'
import toast from 'react-hot-toast'

function formatStandLabel(stand: string | null): string {
  if (!stand) return 'Heute'
  const d = new Date(stand + 'T12:00:00')
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export default function Units() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = (searchParams.get('type') || 'class') as 'class' | 'course' | 'group' | 'school-year-transition'
  const standParam = searchParams.get('stand')
  const [standAsOf, setStandAsOf] = useState<string | null>(() => (standParam && /^\d{4}-\d{2}-\d{2}$/.test(standParam) ? standParam : null))
  const [standDropdownOpen, setStandDropdownOpen] = useState(false)
  const [standDatePicker, setStandDatePicker] = useState('')

  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: schoolYearsResponse } = useQuery(
    ['school-years'],
    () => api.get<{ success: boolean; data: { id: number; name: string; start_date: string; end_date: string }[] }>('/school-years').then(res => res.data),
    { enabled: standDropdownOpen, staleTime: 60_000 }
  )
  const schoolYears = schoolYearsResponse?.data ?? []

  const isAdmin = user?.role === 'administrator' || user?.role === 'admin'
  const { data: switchSuggestionResponse } = useQuery(
    ['school-years', 'switch-suggestion'],
    () => api.get<{ success: boolean; data: { suggestSwitch: boolean; reason: string | null; periodName: string } }>('/school-years/switch-suggestion').then(res => res.data),
    { staleTime: 60_000, enabled: isAdmin }
  )
  const switchSuggestion = switchSuggestionResponse?.data

  const standLabel = useMemo(() => {
    if (standAsOf) return formatStandLabel(standAsOf)
    return 'Heute'
  }, [standAsOf])

  useEffect(() => {
    const p = searchParams.get('stand')
    if (p && /^\d{4}-\d{2}-\d{2}$/.test(p)) setStandAsOf(p)
    else if (!p) setStandAsOf(null)
  }, [searchParams])

  const applyStand = (value: string | null) => {
    setStandAsOf(value)
    setStandDropdownOpen(false)
    if (value) searchParams.set('stand', value)
    else searchParams.delete('stand')
    setSearchParams(searchParams)
  }

  const ldapSyncMutation = useMutation(
    () => api.post('/units/ldap-sync'),
    {
      onSuccess: (res) => {
        const d = res.data?.data
        queryClient.invalidateQueries(['class'])
        toast.success(
          <div>
            <div className="font-semibold">LDAP-Sync Schulstruktur abgeschlossen</div>
            <div className="text-sm mt-1">
              {d?.classesCreated ?? 0} neue Klassen, {(d?.classesExisting ?? 0)} bestehend • {d?.membershipsCreated ?? 0} Zugehörigkeiten für {d?.studentsProcessed ?? 0} Schüler
            </div>
          </div>,
          { duration: 5000 }
        )
      },
      onError: (err: any) => {
        if (err?.response?.status === 403) {
          toast.error('Nur Administratoren können den LDAP-Sync ausführen.')
        } else if (err?.response?.status === 401) {
          toast.error('Bitte melden Sie sich erneut an')
        } else {
          toast.error(err?.response?.data?.message || 'LDAP-Sync fehlgeschlagen')
        }
      }
    }
  )

  const handleLdapSync = () => {
    if (confirm('LDAP-Verbindung herstellen und Klassen sowie Mitglieder aus dem LDAP-Verzeichnis übernehmen? Dies kann einen Moment dauern.')) {
      ldapSyncMutation.mutate()
    }
  }

  const tabs = [
    { 
      id: 'class' as const, 
      name: 'Klassen', 
      icon: GraduationCap,
      unitType: 'class' as const,
      title: 'Klassen',
      description: 'Verwalten Sie Klassen und Schülerzuordnungen',
      createButtonLabel: 'Neue Klasse',
      searchPlaceholder: 'Nach Klassenname, Stufe oder Abteilung suchen...'
    },
    { 
      id: 'course' as const, 
      name: 'Kurse', 
      icon: Users,
      unitType: 'course' as const,
      title: 'Kurse',
      description: 'Verwalten Sie Kurse für eine bessere Schülerorganisation',
      createButtonLabel: 'Neuer Kurs',
      searchPlaceholder: 'Nach Kursname, Fach oder Jahrgang suchen...'
    },
    { 
      id: 'group' as const, 
      name: 'Gruppen', 
      icon: UserCircle,
      unitType: 'group' as const,
      title: 'Gruppen',
      description: 'Verwalten Sie Gruppen für flexible Schülerorganisation',
      createButtonLabel: 'Neue Gruppe',
      searchPlaceholder: 'Nach Gruppenname, Typ oder Beschreibung suchen...'
    },
    ...(isAdmin ? [{
      id: 'school-year-transition' as const,
      name: 'Schuljahreswechsel',
      icon: Calendar,
      unitType: null,
      title: 'Schuljahreswechsel',
      description: 'Zentraler Ablauf für den Wechsel in das neue Schuljahr',
      createButtonLabel: '',
      searchPlaceholder: ''
    }] : [])
  ]

  const handleTabChange = (tabId: 'class' | 'course' | 'group' | 'school-year-transition') => {
    setSearchParams({ type: tabId })
  }

  const safeActiveTab = activeTab === 'school-year-transition' && !isAdmin ? 'class' : activeTab
  const activeTabConfig = tabs.find(tab => tab.id === safeActiveTab) || tabs[0]
  const isSchoolYearTransition = safeActiveTab === 'school-year-transition'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-50">Lerngruppen</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Verwalten Sie Klassen, Kurse und Gruppen sowie deren Mitgliedschaften
          </p>
        </div>
        {activeTab === 'class' && (user?.role === 'administrator' || user?.role === 'admin') && (
          <button
            type="button"
            onClick={handleLdapSync}
            disabled={ldapSyncMutation.isLoading}
            className="btn btn-primary flex items-center gap-2 shrink-0"
          >
            <Database className="h-4 w-4" />
            {ldapSyncMutation.isLoading ? 'Synchronisiere...' : 'LDAP-Sync (Klassen & Mitglieder)'}
          </button>
        )}
      </div>

      {/* Admin-Hinweis: Stichtag überschritten – Umstellen? */}
      {!isSchoolYearTransition && isAdmin && switchSuggestion?.suggestSwitch && switchSuggestion.reason && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{switchSuggestion.reason}</p>
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
              Aktueller Abschnitt: <strong>{switchSuggestion.periodName}</strong>. Möchten Sie umstellen?
            </p>
            {!isSchoolYearTransition && (
              <button
                type="button"
                onClick={() => handleTabChange('school-year-transition')}
                className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Zum Schuljahreswechsel wechseln →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Zeitkontext (nur bei Klassen/Kursen/Gruppen) */}
      {!isSchoolYearTransition && (
        <div className="relative flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">Stand:</span>
          <button
            type="button"
            onClick={() => setStandDropdownOpen(!standDropdownOpen)}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            {standLabel}
            <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </button>
          {standDropdownOpen && (
            <>
              <div className="fixed inset-0 z-10" aria-hidden onClick={() => setStandDropdownOpen(false)} />
              <div className="absolute left-0 top-full z-20 mt-1 w-72 rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40">
                <button
                  type="button"
                  onClick={() => applyStand(null)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  Heute (Standard)
                </button>
                <div className="border-t border-gray-100 px-4 py-2 dark:border-gray-800">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Stichtag</label>
                  <input
                    type="date"
                    value={standDatePicker}
                    onChange={(e) => setStandDatePicker(e.target.value)}
                    className="input-field mt-1 py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (standDatePicker) applyStand(standDatePicker)
                    }}
                    disabled={!standDatePicker}
                    className="mt-2 w-full rounded bg-primary-600 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    Anwenden
                  </button>
                </div>
                {schoolYears.length > 0 && (
                  <div className="border-t border-gray-100 px-4 py-2 dark:border-gray-800">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Schuljahr (Anfang)</label>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {schoolYears.map((sy: { id: number; name: string; start_date: string }) => (
                        <button
                          key={sy.id}
                          type="button"
                          onClick={() => applyStand(sy.start_date)}
                          className="rounded bg-gray-100 px-2 py-1 text-sm text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                        >
                          {sy.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.name}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {isSchoolYearTransition ? (
        <SchoolYearTransitionTab />
      ) : (
        <UnitManagementPage
          unitType={activeTabConfig.unitType!}
          title={activeTabConfig.title}
          description={activeTabConfig.description}
          createButtonLabel={activeTabConfig.createButtonLabel}
          searchPlaceholder={activeTabConfig.searchPlaceholder}
          standAsOf={standAsOf}
        />
      )}
    </div>
  )
}

